import { MapContext } from 'contexts/MapContext';
import * as Styled from './styles';
import * as RiIcons from 'react-icons/ri';
import React, { useContext, useMemo, useEffect, useState } from 'react';
import { useLayers } from 'hooks/useLayers';

import VectorLayer from 'ol/layer/Vector';
import { ToolBox } from 'screens/Dashboard/components/SideBar/ToolBox';
import { BaseSelect } from 'components/BaseSelect';

function getName(str: string) {
  const key = 'p3m:';
  const index = str?.indexOf(key);
  return index !== -1 ? 'p3m:' + str?.slice(index + key.length) : '';
}

function getLayerId(layer: any): string {
  const props = layer?.getProperties?.() ?? {};

  if (props.layertitle) return String(props.layertitle);
  if (props.uuid) return String(props.uuid);

  return '';
}

export function MergeFeaturesMenu() {
  const { getLayerAttritubeByName } = useLayers();

  const {
    map,
    mergeLayer,
    handleSetMergeLayer,
    selectedFeatures,
    mergeError,
    isMerging,
    mergeSelected,
  } = useContext(MapContext);

  const [layersVersion, setLayersVersion] = useState(0);

  useEffect(() => {
    if (!map) return;

    const layers = map.getLayers();

    const onAdd = () => {
      setLayersVersion(v => v + 1);
    };

    layers.on('add', onAdd);

    return () => {
      layers.un('add', onAdd);
    };
  }, [map]);

  const vectorLayers = useMemo(() => {
    if (!map) return [];

    return map
      .getLayers()
      .getArray()
      .filter((layer: any) => {
        if (!(layer instanceof VectorLayer)) return false;

        const props = layer?.getProperties?.() ?? {};
        return !!(props.layertitle || props.uuid);
      });
  }, [map, layersVersion]);

  const selectedId = mergeLayer ? getLayerId(mergeLayer) : '';

  function getLayerLabel(layer: any) {
    const id = getLayerId(layer);
    const meta = getLayerAttritubeByName(getName(id))?.layertitle || id;
    return meta.replace(/\.(kml|geojson|zip|shp)$/i, '');
  }

  const canMerge = selectedFeatures.length >= 2 && !isMerging;

  const errorMessages: Record<string, string> = {
    'not-touching': 'As feições selecionadas não se tocam.',
    'not-polygon': 'Apenas feições do tipo Polígono podem ser mescladas.',
  };

  return (
    <ToolBox
      header={
        <>
          <RiIcons.RiMergeCellsHorizontal />
          <span>Unir Feições</span>
        </>
      }
    >
      <>
        <Styled.MergeGroup>
          <BaseSelect
            label='Camada vetorial'
            value={selectedId || 'disable'}
            onChange={handleSetMergeLayer}
            placeholder={'Selecione a camada'}
            options={vectorLayers.map(l => ({ label: getLayerLabel(l), value: getLayerId(l) }))}
          />
        </Styled.MergeGroup>

        {mergeLayer && (
          <>
            <Styled.MergeGroup>
              <label>
                {selectedFeatures.length === 0
                  ? 'Clique nas feições para selecioná-las'
                  : `${selectedFeatures.length} feição(ões) selecionada(s)`}
              </label>
            </Styled.MergeGroup>

            {mergeError && (
              <Styled.MergeGroup>
                <span style={{ color: 'red', fontSize: '1.2rem' }}>
                  {errorMessages[mergeError] ?? 'Erro ao mesclar feições.'}
                </span>
              </Styled.MergeGroup>
            )}

            {isMerging && (
              <Styled.MergeGroup>
                <span style={{ fontSize: '1.2rem' }}>
                  Processando uniao das feicoes...
                </span>
              </Styled.MergeGroup>
            )}

            <Styled.MergeGroup style={{ flexDirection: 'row', gap: '0.5rem' }}>
              <Styled.ApplyButton
                onClick={mergeSelected}
                disabled={!canMerge}
                title={!canMerge && !isMerging ? 'Selecione ao menos duas feições' : 'Aplicar união das feições selecionadas'}
              >
                {isMerging ? 'Processando...' : 'Aplicar união'}
              </Styled.ApplyButton>
            </Styled.MergeGroup>
          </>
        )}
      </>
    </ToolBox>
  );
}